import { useState } from "react";
import {
  Link as RouterLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router";
import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { keyframes } from "@mui/material/styles";
import {
  LogoutOutlined as LogoutIcon,
  GridViewOutlined as OverviewIcon,
  DescriptionOutlined as ContractsIcon,
  DashboardOutlined as ObjectsIcon,
  ArticleOutlined as DocumentsIcon,
  HeadsetMicOutlined as ContactIcon,
  KeyboardArrowDown as ChevronDownIcon,
  MenuOpenOutlined as MenuOpenIcon,
  MenuOutlined as MenuIcon,
  CloseOutlined as CloseIcon,
} from "@mui/icons-material";

import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { PORTAL } from "@accredo/fe-shared/theme/portals.js";
import {
  sidebarWidths,
  colorsFoundation,
  borderRadius,
  widths,
} from "@accredo/fe-shared/theme/index.js";
import { useLogout } from "@accredo/fe-shared/hooks/useLogout.js";
import Breadcrumbs from "@accredo/fe-shared/components/Breadcrumbs.jsx";
import Footer from "@accredo/fe-shared/components/Footer.jsx";
import UserMenu from "@accredo/fe-shared/components/UserMenu.jsx";
import ContactModal from "@accredo/fe-shared/components/contact/ContactModal.jsx";
import PortalSwitcher from "@accredo/fe-shared/components/PortalSwitcher.jsx";
import logo_src from "@accredo/fe-shared/assets/logo.svg";
import sidebar_logo_src from "@accredo/fe-shared/assets/logo_inverted.svg";

import CustomerPicker from "./components/CustomerPicker.jsx";
import {
  readCustomerSelection,
  writeCustomerSelection,
} from "./customerScope.js";

const NAV_ENTRIES = [
  { label: "Übersicht", to: "/", icon: OverviewIcon },
  { label: "Verträge", to: "/vertraege", icon: ContractsIcon },
  { label: "Objekte", to: "/objekte", icon: ObjectsIcon },
  { label: "Dokumente", to: "/dokumente", icon: DocumentsIcon },
];

const ROLE_LABELS = {
  kd_verwaltung: "Verwaltung",
  kd_kundenportal_eingeschraenkt: "Kunde",
};

const Frame = styled(Box)({
  display: "flex",
  height: "100vh",
  overflow: "hidden",
});

const Sidebar = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCollapsed" && prop !== "isMobile",
})(({ theme, isCollapsed, isMobile }) => ({
  width: isMobile
    ? "100%"
    : isCollapsed
      ? sidebarWidths.minimized
      : sidebarWidths.expanded,
  height: isMobile ? "100%" : undefined,
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.sidebar,
  transition: "width 0.3s ease",
}));

const Nav = styled("nav")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  padding: theme.spacing(1.5),
}));

const NavLink = styled(RouterLink, {
  shouldForwardProp: (prop) => prop !== "isCurrent" && prop !== "isCollapsed",
})(({ theme, isCurrent, isCollapsed }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: isCollapsed ? "center" : "flex-start",
  gap: theme.spacing(1.25),
  padding: isCollapsed ? theme.spacing(1.25) : theme.spacing(1.25, 1.5),
  borderRadius: borderRadius.sm,
  textDecoration: "none",
  fontWeight: isCurrent ? 600 : 400,
  color: isCurrent
    ? theme.palette.sidebarNav.text.active
    : theme.palette.sidebarNav.text.default,
  backgroundColor: isCurrent
    ? theme.palette.sidebarNav.background.active
    : theme.palette.sidebarNav.background.default,
  "& .MuiSvgIcon-root": {
    fontSize: "1.25rem",
    color: isCurrent
      ? theme.palette.sidebarNav.icon.active
      : theme.palette.sidebarNav.icon.default,
  },
  "&:hover": {
    color: theme.palette.sidebarNav.text.hover,
    backgroundColor: theme.palette.sidebarNav.background.hover,
    "& .MuiSvgIcon-root": {
      color: theme.palette.sidebarNav.icon.hover,
    },
  },
}));

const ContactArea = styled(Box)(({ theme }) => ({
  marginTop: "auto",
  padding: theme.spacing(1.5),
}));

const MobileDrawerHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  padding: theme.spacing(1.5, 1.5, 0),
}));

const BrandArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCollapsed",
})(({ theme, isCollapsed }) => ({
  display: "flex",
  justifyContent: isCollapsed ? "center" : "flex-start",
  padding: isCollapsed ? theme.spacing(3, 0, 1.5) : theme.spacing(3, 3, 1.5),
}));

const BrandMark = styled(Box)({
  height: 56,
  display: "flex",
  alignItems: "center",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
});

const PortalArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCollapsed",
})(({ theme, isCollapsed }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: isCollapsed ? "center" : "space-between",
  // Right edge in line with the navigation entries below.
  padding: isCollapsed ? theme.spacing(0, 0, 2) : theme.spacing(0, 1.5, 2, 3),
}));

const SidebarToggle = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "isCollapsed",
})(({ theme, isCollapsed }) => ({
  padding: theme.spacing(0.5),
  marginLeft: isCollapsed ? 0 : "auto",
  color: colorsFoundation.key.blue[200],
  transform: isCollapsed ? "scaleX(-1)" : "none",
  transition: "transform 0.3s ease",
  "&:hover": {
    color: theme.palette.sidebarNav.text.hover,
    backgroundColor: theme.palette.sidebarNav.background.hover,
  },
}));

const MainColumn = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
});

const Header = styled("header")(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 10,
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2, 2),
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(2, 5),
  },
}));

const UserButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  background: "none",
  border: 0,
  padding: 0,
  cursor: "pointer",
  font: "inherit",
  textAlign: "left",
  color: "inherit",
}));

const UserAvatar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: "50%",
  backgroundColor: theme.palette.grey[100],
}));

const UserAvatarText = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 700,
  color: theme.palette.primary.main,
}));

const UserInfo = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const UserName = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const UserRole = styled(Typography)(({ theme }) => ({
  fontSize: "0.8125rem",
  color: theme.palette.text.secondary,
}));

const Content = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
});

// The page's padding, kept off the footer so it spans the full width at the
// bottom edge, as in the Schadenportal. Narrower left/right/top on mobile, as
// in the Schadenportal, so more content fits on screen.
const ContentPadding = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2, 2, 5, 2),
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(5, 5, 5, 5),
  },
}));

const pageFadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

// A plain route swap with no transition at all reads as a hard cut - this is
// deliberately just an opacity fade (no movement/layout change) so it stays
// unobtrusive and doesn't delay the page being usable.
const ContentInner = styled(Box)({
  width: "100%",
  flexShrink: 0,
  maxWidth: widths.xl,
  margin: "0 auto",
  animation: `${pageFadeIn} 150ms ease-out`,
});

const initialsOf = (user) =>
  [user?.vorname, user?.nachname]
    .filter(Boolean)
    .map((part) => part.trim().charAt(0).toUpperCase())
    .join("");

const roleLabelOf = (user) =>
  (user?.role_names ?? []).map((role) => ROLE_LABELS[role]).find(Boolean) ?? "";

function NavEntry({ to, label, icon: Icon, isCollapsed, ...link_props }) {
  const link = (
    <NavLink to={to} isCollapsed={isCollapsed} {...link_props}>
      <Icon />
      {!isCollapsed && label}
    </NavLink>
  );

  return isCollapsed ? (
    <Tooltip title={label} placement="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
}

/**
 * The frame around every Kundenportal page: sidebar, header, content, footer.
 * From md up the sidebar collapses to icons; the state is not kept across
 * page loads.
 *
 * The portal switcher only appears for someone who may use both portals. For
 * everyone else there is no mention of the Schadenportal at all — hidden, not
 * disabled.
 */
export default function CustomerPortalLayout() {
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const is_desktop = useMediaQuery(theme.breakpoints.up("md"));
  const [is_minimized, setIsMinimized] = useState(false);
  const is_collapsed = is_minimized && is_desktop;
  const handleLogout = useLogout();
  const [user_menu_anchor_el, setUserMenuAnchorEl] = useState(null);
  const [drawer_open, setDrawerOpen] = useState(false);
  const [contact_open, setContactOpen] = useState(false);
  const [customer_selection, setCustomerSelection] = useState(
    readCustomerSelection
  );
  const can_select_customer = Boolean(user?.can_select_customer);

  // A different scope starts from the dashboard; the selection id is part of
  // the content key below, so nothing loaded for the previous scope stays.
  const handleCustomerChange = (selection) => {
    writeCustomerSelection(selection);
    setCustomerSelection(selection);
    navigate("/");
  };

  const customer_picker = (
    <CustomerPicker
      selection={customer_selection}
      onChange={handleCustomerChange}
    />
  );

  const isCurrent = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const closeDrawerIfMobile = () => {
    if (!is_desktop) {
      setDrawerOpen(false);
    }
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const sidebarContent = (
    <>
      <BrandArea isCollapsed={is_collapsed}>
        <BrandMark>
          <img src={sidebar_logo_src} alt="Logo" />
        </BrandMark>
      </BrandArea>

      <PortalArea isCollapsed={is_collapsed}>
        {!is_collapsed && <PortalSwitcher current={PORTAL.KUNDENPORTAL} />}
        {is_desktop && (
          <SidebarToggle
            size="small"
            isCollapsed={is_collapsed}
            aria-label={
              is_collapsed
                ? "Seitenleiste ausklappen"
                : "Seitenleiste einklappen"
            }
            onClick={() => setIsMinimized((minimized) => !minimized)}
          >
            <MenuOpenIcon />
          </SidebarToggle>
        )}
      </PortalArea>

      <Nav>
        {NAV_ENTRIES.map((entry) => (
          <NavEntry
            key={entry.to}
            {...entry}
            isCollapsed={is_collapsed}
            isCurrent={isCurrent(entry.to)}
            aria-current={isCurrent(entry.to) ? "page" : undefined}
            onClick={closeDrawerIfMobile}
          />
        ))}
      </Nav>

      <ContactArea>
        <NavEntry
          to="#"
          label="Kontakt"
          icon={ContactIcon}
          isCollapsed={is_collapsed}
          onClick={(event) => {
            event.preventDefault();
            closeDrawerIfMobile();
            setContactOpen(true);
          }}
        />
      </ContactArea>
    </>
  );

  return (
    <Frame>
      {is_desktop ? (
        <Sidebar data-testid="sidebar" isCollapsed={is_collapsed}>
          {sidebarContent}
        </Sidebar>
      ) : (
        <Drawer
          anchor="left"
          open={drawer_open}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: "100%", maxWidth: "100%" } }}
        >
          <Sidebar data-testid="sidebar" isCollapsed={false} isMobile>
            <MobileDrawerHeader>
              <IconButton
                onClick={() => setDrawerOpen(false)}
                aria-label="Navigation schließen"
                sx={{
                  color: (theme) => theme.palette.sidebarNav.text.active,
                  "&:hover": { color: colorsFoundation.key.blue[200] },
                }}
              >
                <CloseIcon />
              </IconButton>
            </MobileDrawerHeader>
            {sidebarContent}
          </Sidebar>
        </Drawer>
      )}

      <MainColumn>
        <Header>
          {!is_desktop && (
            <>
              <IconButton
                onClick={() => setDrawerOpen(true)}
                aria-label="Navigation öffnen"
                sx={{ ml: "-8px" }}
              >
                <MenuIcon />
              </IconButton>
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  ml: 1,
                  mr: "auto",
                  height: 40,
                  textDecoration: "none",
                }}
              >
                <img src={logo_src} alt="Logo" style={{ height: 40 }} />
              </Box>
            </>
          )}
          {is_desktop && <Breadcrumbs />}
          {is_desktop && can_select_customer && (
            <Box sx={{ ml: "auto", mr: 2, minWidth: 0 }}>{customer_picker}</Box>
          )}
          <UserButton
            type="button"
            aria-haspopup="menu"
            aria-expanded={Boolean(user_menu_anchor_el)}
            onClick={handleUserMenuOpen}
          >
            <UserAvatar>
              <UserAvatarText>{initialsOf(user)}</UserAvatarText>
            </UserAvatar>
            <UserInfo>
              <UserName>
                {`${user?.vorname ?? ""} ${user?.nachname ?? ""}`.trim()}
              </UserName>
              <UserRole>{roleLabelOf(user)}</UserRole>
            </UserInfo>
            <ChevronDownIcon
              fontSize="small"
              sx={{ color: "text.secondary" }}
            />
          </UserButton>
          <UserMenu
            anchorEl={user_menu_anchor_el}
            open={Boolean(user_menu_anchor_el)}
            onClose={handleUserMenuClose}
            menuItems={[
              { label: "Abmelden", icon: LogoutIcon, onClick: handleLogout },
            ]}
          />
        </Header>
        <Content component="main">
          <ContentPadding>
            <ContentInner
              key={`${location.pathname}:${customer_selection?.id ?? ""}`}
            >
              {!is_desktop && (
                <Box sx={{ mb: 2.5 }}>
                  <Breadcrumbs />
                </Box>
              )}
              {!is_desktop && can_select_customer && (
                <Box sx={{ mb: 2.5 }}>{customer_picker}</Box>
              )}
              <Outlet />
            </ContentInner>
          </ContentPadding>
          <Footer />
        </Content>
      </MainColumn>

      <ContactModal open={contact_open} onClose={() => setContactOpen(false)} />
    </Frame>
  );
}
